// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/types/index.ts
//  Tipos alinhados com os DTOs da API Udisys — módulo DAV (Pedidos de Venda).
// ════════════════════════════════════════════════════════════════════════════

// ── Respostas ─────────────────────────────────────────────────────────────

export interface DavResumo {
  id: number;
  numeroDav: number;
  vendedorId: number;
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  dtEmissao: string;
  totalGeral?: number;
  totalDesconto?: number;
  totalLiquido?: number;
  isBaixada: boolean;
  isPedidoEntregue: boolean;
  /** DAV importado por nota fiscal — rascunho travado (itens/parcelas congelados). */
  isImportadoNf: boolean;
  /** DAV estornado (estado terminal — não pode mais ser alterado nem reaberto). */
  isEstornado: boolean;
  dtEstorno?: string | null;
  motivoEstorno?: string | null;
  dtCriacao: string;
}

export interface EstornarDavRequest {
  motivoEstorno: string;
}

export interface DavProduto {
  id: number;
  produtoId: number;
  subProdutoId: number;
  vendedorId?: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorTotal: number;
  produtoDescricao?: string | null;
}

export interface DavFinanceiroParcela {
  id: number;
  nroParcela: number;
  qtdParcelas: number;
  dtVencimento?: string | null;
  valorReceber: number;
  valorOriginal: number;
  isPago: boolean;
  formaPagRecId?: number | null;
}

/** Dados de entrega do DAV (endereço completo + agendamento). */
export interface DavEntrega {
  endereco?: string | null;
  numero?: string | null;
  bairro?: string | null;
  complemento?: string | null;
  cep?: string | null;
  cidade?: string | null;
  uf?: string | null;
  veiculoId?: number | null;
  rotaId?: number | null;
  dtEntrega?: string | null;
  horaEntrega?: string | null;
  observacao?: string | null;
}

export interface DavDetalhe extends DavResumo {
  perComissaoVendedor: number;
  vendedor2Id?: number;
  perComissaoVendedor2?: number;
  fone?: string;
  celular?: string;
  observacao?: string;
  totalAcrescimo?: number;
  formaPagRecId?: number;
  condicaoPagamentoId?: number;
  parcela?: number;
  diasParcela?: number;
  dtAtualizacao: string;
  produtos: DavProduto[];
  financeiros: DavFinanceiroParcela[];
  entrega?: DavEntrega | null;
}

// ── Requisições ───────────────────────────────────────────────────────────

export interface DavProdutoRequest {
  produtoId: number;
  subProdutoId: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  vendedorId?: number;
}

/** Parcela informada pelo form (override da geração automática do servidor). */
export interface DavParcelaRequest {
  nroParcela: number;
  dtVencimento: string;
  valor: number;
  formaPagRecId?: number;
}

/** Entrega informada no pedido (endereço completo + agendamento). */
export interface DavEntregaRequest {
  endereco?: string;
  numero?: string;
  bairro?: string;
  complemento?: string;
  cep?: string;
  cidade?: string;
  uf?: string;
  veiculoId?: number;
  rotaId?: number;
  dtEntrega?: string;
  horaEntrega?: string;
  observacao?: string;
}

export interface CriarDavRequest {
  vendedorId: number;
  dtEmissao: string;
  produtos: DavProdutoRequest[];
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  formaPagRecId?: number;
  condicaoPagamentoId?: number;
  parcela?: number;
  diasParcela?: number;
  /** Desconto/acréscimo GERAL (flat, R$) — somado aos valores por item nos totais. */
  descontoGeral?: number;
  acrescimoGeral?: number;
  vendedor2Id?: number;
  perComissaoVendedor?: number;
  perComissaoVendedor2?: number;
  /** Override das parcelas; se ausente, o servidor gera de parcela/diasParcela/total. */
  parcelas?: DavParcelaRequest[];
  /** Entrega (endereço + agendamento). */
  entrega?: DavEntregaRequest;
}

export interface AtualizarDavRequest {
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  formaPagRecId?: number;
  condicaoPagamentoId?: number;
  parcela?: number;
  diasParcela?: number;
  isBaixada?: boolean;
  isPedidoEntregue?: boolean;
  vendedor2Id?: number;
  perComissaoVendedor?: number;
  perComissaoVendedor2?: number;
  /** Override das parcelas; regenera o financeiro do rascunho (DAV não baixado). */
  parcelas?: DavParcelaRequest[];
  /** Itens do rascunho — substitui os itens e recalcula totais + financeiro. */
  produtos?: DavProdutoRequest[];
  descontoGeral?: number;
  acrescimoGeral?: number;
  /** Entrega (endereço + agendamento) — upsert do registro de entrega. */
  entrega?: DavEntregaRequest;
}

// ── Helpers ───────────────────────────────────────────────────────────────

export type StatusDav = 'aberto' | 'baixado' | 'entregue';

export function calcularStatusDav(isBaixada: boolean, isPedidoEntregue: boolean): StatusDav {
  if (isBaixada && isPedidoEntregue) return 'entregue';
  if (isBaixada) return 'baixado';
  return 'aberto';
}

// ── Paginação ─────────────────────────────────────────────────────────────

export interface PaginadoResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

// ════════════════════════════════════════════════════════════════════════════
//  ORÇAMENTO
// ════════════════════════════════════════════════════════════════════════════

export interface OrcamentoResumo {
  id: number;
  numeroOrcamento: number;
  vendedorId: number;
  clienteId: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  dtEmissao: string;
  dtValidade: string;
  totalGeral: number;
  totalLiquido: number;
  isBaixada: boolean;
  isDevolvido: boolean;
  isOrcamento: boolean;
  dtCriacao: string;
}

export interface OrcamentoProduto {
  id: number;
  orcamentoId: number;
  produtoId: number;
  subProdutoId: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorTotal: number;
  observacao?: string;
  produtoDescricao?: string;
}

export interface OrcamentoParcela {
  nroParcela: number;
  dtVencimento: string;
  valor: number;
  formaPagRecId?: number;
}

export interface OrcamentoDetalhe extends OrcamentoResumo {
  fone?: string;
  celular?: string;
  observacao?: string;
  dtPrevisao: string;
  totalDesconto: number;
  totalAcrescimo: number;
  produtos: OrcamentoProduto[];
  formaPagRecId?: number;
  qtdParcelas?: number;
  parcelas?: OrcamentoParcela[];
}

export interface OrcamentoProdutoRequest {
  produtoId: number;
  subProdutoId: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  observacao?: string;
}

export interface OrcamentoParcelaRequest {
  nroParcela: number;
  dtVencimento: string;
  valor: number;
  formaPagRecId?: number;
}

export interface CriarOrcamentoRequest {
  vendedorId: number;
  dtEmissao: string;
  dtValidade: string;
  dtPrevisao: string;
  produtos: OrcamentoProdutoRequest[];
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  formaPagRecId?: number;
  qtdParcelas?: number;
  diasParcela?: number;
  parcelas?: OrcamentoParcelaRequest[];
}

export interface AtualizarOrcamentoRequest {
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  dtValidade?: string;
  isBaixada?: boolean;
  isDevolvido?: boolean;
  formaPagRecId?: number;
  qtdParcelas?: number;
  diasParcela?: number;
  parcelas?: OrcamentoParcelaRequest[];
}

export type StatusOrcamento = 'aberto' | 'aprovado' | 'expirado' | 'devolvido';

export function calcularStatusOrcamento(isBaixada: boolean, isDevolvido: boolean, dtValidade: string): StatusOrcamento {
  if (isDevolvido) return 'devolvido';
  if (isBaixada) return 'aprovado';
  if (new Date(dtValidade) < new Date()) return 'expirado';
  return 'aberto';
}

// ════════════════════════════════════════════════════════════════════════════
//  ENTREGA
// ════════════════════════════════════════════════════════════════════════════

export interface EntregaResumo {
  id: number;
  numeroEntrega: number;
  clienteId: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  dtMovimento: string;
  dtEntrega: string;
  horaEntrega?: string;
  cidade?: string;
  uf?: string;
  bairro?: string;
  endereco?: string;
  numero?: string;
  valorTotalCarga: number;
  valorTotalConferido: number;
  motorista?: string;
  isAberta: boolean;
  isConferido: boolean;
  isFinalizado: boolean;
  isCancelada: boolean;
  isRetirarLoja: boolean;
  dtCriacao: string;
}

export interface EntregaProduto {
  id: number;
  entregaId: number;
  produtoId: number;
  subProdutoId: number;
  valorUnitario: number;
  quantidade: number;
  valorTotal: number;
  quantidadeSaldo: number;
  quantidadeConferida: number;
  isConferido: number;
  observacao?: string | null;
}

export interface ConferirEntregaProdutoRequest {
  isConferido: boolean;
  quantidadeConferida?: number | null;
  observacao?: string | null;
}

export interface FecharConferenciaEntregaRequest {
  observacao?: string | null;
}

export interface EntregaRecebimento {
  id: number;
  formaPagRecId: number;
  formaPagRec?: string;
  valor: number;
  dtMovimento: string;
  isRecebido: boolean;
  linkPagamento?: string;
}

export interface EntregaDetalhe extends EntregaResumo {
  celular?: string;
  cep?: string;
  complemento?: string;
  observacao?: string;
  taxaEntrega: number;
  valorRecebido: number;
  troco: number;
  qtdDivergencia: number;
  valorDivergencia: number;
  isRecebido: boolean;
  dtSaida?: string | null;
  dtConferencia?: string | null;
  dtRetorno?: string | null;
  produtos: EntregaProduto[];
  recebimentos: EntregaRecebimento[];
}

export interface AtualizarStatusEntregaRequest {
  isAberta?: boolean;
  isFinalizado?: boolean;
  isConferido?: boolean;
  isCancelada?: boolean;
}

export type StatusEntrega = 'pendente' | 'em_rota' | 'entregue' | 'cancelada' | 'retirada';

export function calcularStatusEntrega(e: EntregaResumo): StatusEntrega {
  if (e.isCancelada) return 'cancelada';
  if (e.isRetirarLoja && e.isFinalizado) return 'retirada';
  if (e.isFinalizado) return 'entregue';
  if (!e.isAberta) return 'em_rota';
  return 'pendente';
}

// ════════════════════════════════════════════════════════════════════════════
//  ORDEM DE SERVIÇO (OS)
// ════════════════════════════════════════════════════════════════════════════

export interface OsResumo {
  id: number;
  numeroDav: number;
  vendedorId: number;
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  dtEmissao: string;
  totalGeral?: number;
  totalLiquido?: number;
  isBaixada: boolean;
  isPedidoEntregue: boolean;
  dtCriacao: string;
}

export interface OsServico {
  id: number;
  movDavId: number;
  servicoId?: number;
  vendedorId?: number;
  funcionarioId?: number;
  qtdMovimento?: number;
  valorUnitario?: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  valorTotal?: number;
}

export interface OsDetalhe extends OsResumo {
  perComissaoVendedor: number;
  fone?: string;
  celular?: string;
  observacao?: string;
  totalDesconto?: number;
  totalAcrescimo?: number;
  formaPagRecId?: number;
  parcela?: number;
  diasParcela?: number;
  dtAtualizacao: string;
  detalhamentoServico?: string | null;
  acessoriosServico?: string | null;
  observacaoServico?: string | null;
  tipoOrdem?: number | null;
  produtos: DavProduto[];
  servicos: OsServico[];
}

export interface OsServicoRequest {
  servicoId: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  funcionarioId?: number;
}

export interface CriarOsRequest {
  vendedorId: number;
  dtEmissao: string;
  servicos: OsServicoRequest[];
  produtos?: DavProdutoRequest[];
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  formaPagRecId?: number;
  parcela?: number;
  diasParcela?: number;
  detalhamentoServico?: string | null;
  acessoriosServico?: string | null;
  observacaoServico?: string | null;
  tipoOrdem?: number | null;
}

export interface AtualizarOsRequest {
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  formaPagRecId?: number;
  parcela?: number;
  diasParcela?: number;
  detalhamentoServico?: string | null;
  acessoriosServico?: string | null;
  observacaoServico?: string | null;
  tipoOrdem?: number | null;
  isBaixada?: boolean;
  isPedidoEntregue?: boolean;
}

export type StatusOs = 'aberta' | 'em_andamento' | 'concluida' | 'cancelada';

export function calcularStatusOs(isBaixada: boolean, isPedidoEntregue: boolean): StatusOs {
  if (isBaixada && isPedidoEntregue) return 'concluida';
  if (isBaixada) return 'em_andamento';
  return 'aberta';
}

// ── Sub-recursos da OS (equipamento / técnico / acompanhamento) ─────────────

export interface OsEquipamento {
  id: number;
  movDavId: number;
  equipamentoId?: number | null;
  equipamento?: string | null;
  numeroSerie?: string | null;
  certificadoGarantia?: string | null;
  osGarantia?: string | null;
  patrimonio?: string | null;
  licitacao?: string | null;
  dtLancado?: string | null;
}

export interface OsEquipamentoRequest {
  equipamentoId?: number | null;
  equipamento?: string | null;
  numeroSerie?: string | null;
  certificadoGarantia?: string | null;
  osGarantia?: string | null;
  patrimonio?: string | null;
  licitacao?: string | null;
}

export interface OsTecnico {
  id: number;
  movDavId: number;
  tecnicoId: number;
  servicoId: number;
  qtdExecutada?: number | null;
  valorBase?: number | null;
  perComissao?: number | null;
  valorComissao?: number | null;
  totalComissao?: number | null;
  isAcerto: boolean;
}

export interface OsTecnicoRequest {
  tecnicoId: number;
  servicoId: number;
  qtdExecutada?: number | null;
  valorBase?: number | null;
  perComissao?: number | null;
  valorComissao?: number | null;
  totalComissao?: number | null;
}

export interface OsAcompanhamento {
  id: number;
  movDavId: number;
  usuarioId: number;
  status?: string | null;
  obsMudaStatus?: string | null;
  garantia: boolean;
  aprovadaCliente: boolean;
  dtAlteracao: string;
}

export interface OsAcompanhamentoRequest {
  status: string;
  obsMudaStatus?: string | null;
  garantia?: boolean;
  aprovadaCliente?: boolean;
}

export interface OsOcorrencia {
  id: number;
  movDavId: number;
  ocorrencia?: string | null;
  dataOcorrencia?: string | null;
  horaOcorrencia?: string | null;
}

export interface OsOcorrenciaRequest {
  ocorrencia: string;
  dataOcorrencia?: string | null;
  horaOcorrencia?: string | null;
}

export interface OsTarefa {
  id: number;
  movDavId: number;
  descricaoTarefa?: string | null;
  isIniciada: boolean;
  isFinalizada: boolean;
  duracao: number;
  executorId: number;
  temImagemInicio: boolean;
  temImagemFim: boolean;
}

export interface OsTarefaRequest {
  id?: number;
  descricaoTarefa?: string | null;
  isIniciada?: boolean;
  isFinalizada?: boolean;
  duracao?: number;
  executorId?: number;
}

export interface OsDespesa {
  id: number;
  movDavId: number;
  contaDespesasId: number;
  contaContabilId: number;
  documentoOrigem?: string | null;
  valor: number;
  historico?: string | null;
  temImagem: boolean;
}

export interface OsDespesaRequest {
  id?: number;
  valor: number;
  contaDespesasId?: number;
  contaContabilId?: number;
  documentoOrigem?: string | null;
  historico?: string | null;
}

// ── Cadastro: Status da Ordem de Serviço ────────────────────────────────────

export interface StatusOrdemServico {
  id: number;
  descricao: string;
  cor?: string | null;
  contrato?: string | null;
  isAberta: boolean;
  isAndamento: boolean;
  isAgendar: boolean;
  isFaturar: boolean;
  isFechada: boolean;
  isPermissao: boolean;
  isEmailAutomatico: boolean;
  emailAssunto?: string | null;
  emailCorpo?: string | null;
  isInativo: boolean;
}

export interface StatusOrdemServicoCombo {
  id: number;
  descricao: string;
  cor?: string | null;
}

export interface SalvarStatusOrdemServicoRequest {
  descricao: string;
  cor?: string | null;
  contrato?: string | null;
  isAberta?: boolean;
  isAndamento?: boolean;
  isAgendar?: boolean;
  isFaturar?: boolean;
  isFechada?: boolean;
  isPermissao?: boolean;
  isEmailAutomatico?: boolean;
  emailAssunto?: string | null;
  emailCorpo?: string | null;
  isInativo?: boolean;
}

// ════════════════════════════════════════════════════════════════════════════
//  VENDA CONDICIONAL
// ════════════════════════════════════════════════════════════════════════════

export interface CondicionalResumo {
  id: number;
  numeroOrcamento: number;
  vendedorId: number;
  clienteId: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  dtEmissao: string;
  dtValidade: string;
  totalLiquido: number;
  isBaixada: boolean;
  isDevolvido: boolean;
  /** Condicional vencida já reconciliada: o saldo voltou ao depósito (manual ou automático). */
  isExpirada: boolean;
  /** Data da expiração/reconciliação (nula enquanto não expirada). */
  dtExpirada?: string;
  dtCriacao: string;
}

export interface CondicionalProduto {
  id: number;
  orcamentoId: number;
  produtoId: number;
  subProdutoId: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorTotal: number;
  isDevolvido: boolean;
  /** Quantidade já devolvida (acerto parcial). O restante (qtdMovimento − qtdDevolvida) segue com o cliente. */
  qtdDevolvida: number;
  observacao?: string;
}

export interface CondicionalDetalhe extends CondicionalResumo {
  fone?: string;
  celular?: string;
  observacao?: string;
  dtPrevisao: string;
  totalGeral: number;
  totalDesconto: number;
  totalAcrescimo: number;
  produtos: CondicionalProduto[];
}

export interface CondicionalProdutoRequest {
  produtoId: number;
  subProdutoId: number;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  observacao?: string;
}

export interface CriarCondicionalRequest {
  vendedorId: number;
  dtEmissao: string;
  dtValidade: string;
  produtos: CondicionalProdutoRequest[];
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
}

export interface AtualizarCondicionalRequest {
  clienteId?: number;
  nomeRazao?: string;
  cpfCnpj?: string;
  fone?: string;
  celular?: string;
  observacao?: string;
  dtValidade?: string;
  isBaixada?: boolean;
  isDevolvido?: boolean;
}

export interface RegistrarDevolucaoRequest {
  produtoIds: number[];
}

export interface AcertoItemRequest {
  produtoItemId: number;
  /** Quantidade total devolvida (acumulada) deste item. */
  qtdDevolvida: number;
}

export interface AcertoCondicionalRequest {
  itens: AcertoItemRequest[];
  /** Se true, o saldo que ficou com o cliente é vendido (sai da loja) e a condicional é baixada. */
  finalizarVenda?: boolean;
}

export type StatusCondicional = 'aberta' | 'vendida' | 'devolvida' | 'vencida' | 'expirada';

export function calcularStatusCondicional(
  isBaixada: boolean,
  isDevolvido: boolean,
  dtValidade: string,
  isExpirada = false,
): StatusCondicional {
  if (isExpirada) return 'expirada';              // reconciliada: saldo já voltou ao depósito
  if (isDevolvido) return 'devolvida';
  if (isBaixada) return 'vendida';
  if (new Date(dtValidade) < new Date()) return 'vencida';  // validade passou, saldo ainda preso
  return 'aberta';
}

/** Resultado de POST /api/condicionais/expirar-vencidas. */
export interface CondicionalExpiracaoResultado {
  habilitado: boolean;
  processadas: number;
  semSaldo: number;
  condicionaisExpiradas: number[];
}

// ════════════════════════════════════════════════════════════════════════════
//  CRM — ATENDIMENTO / CONTATO
// ════════════════════════════════════════════════════════════════════════════

export interface AtendimentoResumo {
  id: number;
  clienteId: number;
  /** Nome do cliente (só na listagem). */
  clienteNome?: string | null;
  sAssunto?: string;
  prioridadeId: number;
  isResultadoEsperado: boolean;
  dtSolicitacao: string;
  dtCriacao: string;
}

export interface AtendimentoAnalitico {
  id: number;
  atendimentoId: number;
  sTexto?: string;
  dtContato: string;
  dtRetorno: string;
  ibPrecisaRetornar: boolean;
  dtCriacao: string;
}

export interface AtendimentoDetalhe extends AtendimentoResumo {
  sDescricao?: string;
  interacoes: AtendimentoAnalitico[];
}

export interface CriarAtendimentoRequest {
  clienteId: number;
  sAssunto: string;
  sDescricao?: string;
  prioridadeId?: number;
  isResultadoEsperado?: boolean;
  dtSolicitacao?: string;
}

export interface AtualizarAtendimentoRequest {
  sAssunto?: string;
  sDescricao?: string;
  prioridadeId?: number;
  isResultadoEsperado?: boolean;
}

// ── Entregas — Separação por Wave (picking/packing) ─────────────────────────

export const WAVE_STATUS = ['Aberta', 'Em separação', 'Separada', 'Distribuída', 'Cancelada'];
export const WAVE_PACK_STATUS = ['Não iniciado', 'Em conferência', 'Conferido', 'Divergência', 'Pronto p/ carga'];

export interface WaveDavDisponivel {
  movDavId: number;
  numeroDav: number;
  clienteId?: number | null;
  clienteNome?: string | null;
  dtEmissao: string;
  qtdItens: number;
}

export interface WaveResumo {
  id: number;
  status: number;
  qtdPedidos: number;
  serpentina: boolean;
  observacao?: string | null;
  dtCriacao: string;
  dtInicioSeparacao?: string | null;
  dtFimSeparacao?: string | null;
  dtDistribuicao?: string | null;
}

export interface WaveDemanda { movDavId: number; numeroDav: number; clienteNome?: string | null; qtde: number; }

export interface WaveItemConsolidado {
  produtoId: number;
  descricao?: string | null;
  codigoBarras?: string | null;
  endereco?: string | null;
  qtdTotal: number;
  demanda: WaveDemanda[];
}

export interface WavePedido {
  id: number;
  movDavId: number;
  numeroDav: number;
  clienteId?: number | null;
  clienteNome?: string | null;
  statusPack: number;
  qtdItensConferidos: number;
  qtdDivergencias: number;
}

export interface WaveDetalhe {
  id: number;
  status: number;
  localEstoqueId: number;
  serpentina: boolean;
  observacao?: string | null;
  dtCriacao: string;
  dtInicioSeparacao?: string | null;
  dtFimSeparacao?: string | null;
  dtDistribuicao?: string | null;
  pedidos: WavePedido[];
  itensConsolidados: WaveItemConsolidado[];
}

export interface WavePackItem {
  produtoId: number;
  subProdutoId: number;
  descricao?: string | null;
  codigoBarras?: string | null;
  qtdPrevista: number;
  qtdConferida: number;
}

export interface CriarWaveRequest {
  movDavIds: number[];
  localEstoqueId?: number;
  serpentina?: boolean;
  observacao?: string | null;
}

export interface ConferirItemRequest { produtoId: number; subProdutoId: number; qtdConferida: number; }
export interface ConferirPedidoRequest { itens: ConferirItemRequest[]; }

// ── CRM — Campanhas ─────────────────────────────────────────────────────────

export const CAMPANHA_CANAIS = ['WhatsApp', 'E-mail'];
export const CAMPANHA_STATUS = ['Rascunho', 'Enviando', 'Enviada', 'Cancelada', 'Parcial', 'Falha'];
export const CAMPANHA_ENVIO_STATUS = ['Pendente', 'Enviado', 'Erro', 'Ignorado'];

export interface CampanhaResumo {
  id: number;
  nome: string;
  canal: number;
  status: number;
  grupoClienteId?: number | null;
  apenasAniversariantes: boolean;
  dtEnvio?: string | null;
  publicoEstimado: number;
  dtCriacao: string;
}

export interface CampanhaDetalhe extends CampanhaResumo {
  mensagem?: string | null;
  assunto?: string | null;
  classificacao?: string | null;
}

export interface CriarCampanhaRequest {
  nome: string;
  canal?: number;
  mensagem?: string | null;
  assunto?: string | null;
  grupoClienteId?: number | null;
  classificacao?: string | null;
  apenasAniversariantes?: boolean;
}

export type AtualizarCampanhaRequest = Partial<CriarCampanhaRequest>;

export interface PublicoItem { clienteId: number; nome: string; destino?: string | null; }
export interface PublicoPreview { total: number; amostra: PublicoItem[]; }

export interface CampanhaEnvio {
  id: number;
  clienteId: number;
  clienteNome?: string | null;
  destino?: string | null;
  status: number;
  idExterno?: string | null;
  erro?: string | null;
  dtEnvio?: string | null;
}

export interface DispararResultado { total: number; enviados: number; erros: number; ignorados: number; }

// ── CRM — Atividades (agenda) ───────────────────────────────────────────────

export const ATIVIDADE_TIPOS = ['Tarefa', 'Reunião', 'Ligação', 'E-mail', 'Visita', 'Follow-up', 'Outro'];
export const ATIVIDADE_PRIORIDADES = ['Baixa', 'Normal', 'Alta', 'Urgente'];
export const ATIVIDADE_STATUS = ['Pendente', 'Em andamento', 'Concluída', 'Cancelada'];

export interface AtividadeResumo {
  id: number;
  tipo: number;
  titulo: string;
  clienteId?: number | null;
  clienteNome?: string | null;
  responsavelId: number;
  dtInicio: string;
  dtFim?: string | null;
  isDiaInteiro: boolean;
  prioridade: number;
  status: number;
  dtLembrete?: string | null;
}

export interface AtividadeDetalhe extends AtividadeResumo {
  descricao?: string | null;
  resultado?: string | null;
}

export interface CriarAtividadeRequest {
  titulo: string;
  dtInicio: string;
  responsavelId: number;
  tipo?: number;
  clienteId?: number | null;
  dtFim?: string | null;
  isDiaInteiro?: boolean;
  prioridade?: number;
  status?: number;
  dtLembrete?: string | null;
  descricao?: string | null;
  resultado?: string | null;
}

export type AtualizarAtividadeRequest = Partial<CriarAtividadeRequest>;

export interface AtividadeFiltros {
  responsavelId?: number;
  clienteId?: number;
  status?: number;
  de?: string;
  ate?: string;
  /** Início a partir deste dia (yyyy-MM-dd). */
  dtInicio?: string;
  /** Início até este dia, inclusive (yyyy-MM-dd). */
  dtFim?: string;
  /** Título, descrição ou nome do cliente. */
  busca?: string;
  ordenarPor?: 'inicio' | 'titulo' | 'prioridade' | 'status';
  decrescente?: boolean;
}

// ── Visão 360° do cliente (read-only) ──────────────────────────────────────

export interface Visao360Item {
  data: string;
  tipo: string;            // Oportunidade | Atendimento | Compra
  titulo: string;
  descricao?: string | null;
  situacao?: string | null;
}

export interface Visao360Resumo {
  oportunidades: number;
  atendimentos: number;
  compras: number;
  atividades: number;
  campanhas: number;
  whatsapp: number;
  total: number;
  ultimaInteracao?: string | null;
}

export interface Visao360Score {
  score: number;
  faixa: string;           // Quente | Morno | Frio
  fatores: string[];
}

export interface Visao360 {
  clienteId: number;
  clienteNome?: string | null;
  score: Visao360Score;
  resumo: Visao360Resumo;
  timeline: Visao360Item[];
}

export interface AdicionarContatoRequest {
  sTexto: string;
  dtContato: string;
  dtRetorno: string;
  ibPrecisaRetornar?: boolean;
}

// ════════════════════════════════════════════════════════════════════════════
//  CRM — PIPELINE / FUNIL DE VENDAS
// ════════════════════════════════════════════════════════════════════════════

export interface EstagioPipeline {
  id: number;
  nome: string;
  ordem: number;
  cor?: string;
  probabilidadeDefault: number;
  isGanho: boolean;
  isPerda: boolean;
  isAtivo: boolean;
}

export interface CriarEstagioRequest {
  nome: string;
  ordem: number;
  cor?: string;
  probabilidadeDefault?: number;
  isGanho?: boolean;
  isPerda?: boolean;
}

export interface AtualizarEstagioRequest {
  nome?: string;
  ordem?: number;
  cor?: string;
  probabilidadeDefault?: number;
  isGanho?: boolean;
  isPerda?: boolean;
  isAtivo?: boolean;
}

export interface OportunidadeResumo {
  id: number;
  clienteId: number;
  titulo: string;
  estagioId: number;
  vendedorId?: number;
  valorEstimado: number;
  probabilidade: number;
  dtPrevisaoFechamento?: string | null;
  dtFechamento?: string | null;
  origem?: string;
  contato?: string;
  dtCriacao: string;
}

export interface HistoricoOportunidade {
  id: number;
  oportunidadeId: number;
  tipo: string;
  estagioAnteriorId?: number | null;
  estagioNovoId?: number | null;
  valorAnterior?: number | null;
  valorNovo?: number | null;
  observacao?: string | null;
  dtCriacao: string;
}

export interface OportunidadeDetalhe extends OportunidadeResumo {
  descricao?: string;
  motivoPerda?: string;
  atendimentoId?: number;
  historico: HistoricoOportunidade[];
}

export interface CriarOportunidadeRequest {
  clienteId: number;
  titulo: string;
  estagioId: number;
  valorEstimado?: number;
  vendedorId?: number;
  descricao?: string;
  probabilidade?: number;
  dtPrevisaoFechamento?: string;
  origem?: string;
  contato?: string;
  atendimentoId?: number;
}

export interface AtualizarOportunidadeRequest {
  titulo?: string;
  descricao?: string;
  vendedorId?: number;
  valorEstimado?: number;
  probabilidade?: number;
  dtPrevisaoFechamento?: string;
  origem?: string;
  contato?: string;
}

export interface MoverEstagioRequest {
  estagioNovoId: number;
  observacao?: string;
}

export interface FecharOportunidadeRequest {
  ganho: boolean;
  motivoPerda?: string;
  observacao?: string;
}

export interface ColunaPipeline {
  estagio: EstagioPipeline;
  quantidadeOportunidades: number;
  valorTotal: number;
  oportunidades: OportunidadeResumo[];
}

export interface VisaoPipeline {
  colunas: ColunaPipeline[];
}

// ── Dashboard CRM ─────────────────────────────────────────────────────────

export interface CrmKpis {
  totalAtendimentos: number;
  atendimentosPendentes: number;
  atendimentosConcluidos: number;
  retornosAtrasados: number;
  totalOportunidades: number;
  oportunidadesAbertas: number;
  oportunidadesGanhas: number;
  oportunidadesPerdidas: number;
  valorPipelineAberto: number;
  valorGanhoMes: number;
  taxaConversao: number;
  ticketMedio: number;
  cicloVendaDias: number;
}

export interface SerieMensal {
  ano: number;
  mes: number;
  valor: number;
  quantidade: number;
}

export interface FunilEstagio {
  estagioId: number;
  estagioNome: string;
  cor?: string;
  quantidade: number;
  valor: number;
}

export interface DesempenhoVendedor {
  vendedorId: number;
  oportunidadesAbertas: number;
  oportunidadesGanhas: number;
  oportunidadesPerdidas: number;
  valorGanho: number;
  valorPipeline: number;
  taxaConversao: number;
}

export interface OrigemLead {
  origem: string;
  quantidade: number;
  valor: number;
  taxaConversao: number;
}

export interface AlertaCrm {
  tipo: string;
  atendimentoId?: number | null;
  oportunidadeId?: number | null;
  titulo: string;
  descricao: string;
  dtReferencia?: string | null;
  diasAtraso: number;
}

export interface CrmDashboard {
  kpis: CrmKpis;
  funil: FunilEstagio[];
  evolucaoGanhos: SerieMensal[];
  vendedores: DesempenhoVendedor[];
  origens: OrigemLead[];
  alertas: AlertaCrm[];
}

// ════════════════════════════════════════════════════════════════════════════
//  CARGAS (VENDAS EXTERNAS POR ROTA)
// ════════════════════════════════════════════════════════════════════════════

export interface CargaResumo {
  id: number;
  numeroCarga: number;
  veiculoId?: number;
  rotaId?: number;
  motorista?: string;
  dtMontagem: string;
  dtSaida?: string;
  dtRetorno?: string;
  valorTotalCarga: number;
  valorTotalConferido: number;
  qtdTotalSaida: number;
  qtdTotalRetorno: number;
  valorDivergencia: number;
  isAberta: boolean;
  isConferido: boolean;
  isFinalizado: boolean;
  dtCriacao: string;
}

export interface CargaProduto {
  id: number;
  cargaId: number;
  produtoId: number;
  subProdutoId: number;
  valorUnitario: number;
  quantidade: number;
  valorTotal: number;
  quantidadeRetorno: number;
  valorRetorno: number;
  quantidadeDivergente: number;
  isConferido: boolean;
}

export interface CargaDetalhe extends CargaResumo {
  kmSaida: number;
  kmRetorno: number;
  dtConferencia?: string;
  qtdDivergencia: number;
  valorTotalSaida: number;
  produtos: CargaProduto[];
}

export interface CargaProdutoRequest {
  produtoId: number;
  subProdutoId: number;
  quantidade: number;
  valorUnitario: number;
}

export interface IniciarCargaRequest {
  dtMontagem: string;
  produtos: CargaProdutoRequest[];
  veiculoId?: number;
  rotaId?: number;
  motorista?: string;
}

export interface CargaProdutoRetornoRequest {
  produtoId: number;
  quantidadeRetorno: number;
}

export interface RetornarCargaRequest {
  dtRetorno: string;
  produtos: CargaProdutoRetornoRequest[];
}

export interface AtualizarCargaRequest {
  veiculoId?: number;
  motorista?: string;
  dtSaida?: string;
  isConferido?: boolean;
  isFinalizado?: boolean;
}

export type StatusCarga = 'montagem' | 'em_rota' | 'retornada' | 'finalizada';

export function calcularStatusCarga(c: CargaResumo): StatusCarga {
  if (c.isFinalizado) return 'finalizada';
  if (c.dtRetorno) return 'retornada';
  if (c.dtSaida) return 'em_rota';
  return 'montagem';
}

// ════════════════════════════════════════════════════════════════════════════
//  PCP — ORDENS DE PRODUÇÃO
// ════════════════════════════════════════════════════════════════════════════

export interface PcpOrdemResumo {
  id: number;
  numeroOrdem: number;
  clienteId: number;
  isPedidoInterno: boolean;
  custoPrevisto: number;
  custoEfetivo: number;
  quantidadePrevista: number;
  quantidadeEfetiva: number;
  dtEmissao: string;
  dtPrevisaoInicio: string;
  dtPrevisaoTermino: string;
  dtPrevisaoEntrega: string;
  dtInicio?: string;
  dtFim?: string;
  isIniciado: boolean;
  isFinalizado: boolean;
  isCancelado: boolean;
  isParalizado: boolean;
  referencia?: string;
  dtCriacao: string;
}

export interface PcpOrdemItem {
  id: number;
  ordemId: number;
  produtoId: number;
  quantidadePrevista: number;
  quantidadeUtilizada: number;
  quantidadeProduzida: number;
  custoUnitarioPrevisto: number;
  custoTotalPrevisto: number;
  custoUnitarioEfetivo: number;
  custoTotalEfetivo: number;
  isIniciado: boolean;
  isCancelado: boolean;
  isParalizado: boolean;
  isFinalizado: boolean;
  dtInicio?: string;
  dtFinalizacao?: string;
  observacao?: string;
  inconformidades?: string;
  etapaId: number;
}

export interface PcpOrdemDetalhe extends PcpOrdemResumo {
  isSatisfatorio: boolean;
  notaAvaliacao: number;
  observacao?: string;
  inconformidades?: string;
  itens: PcpOrdemItem[];
}

export interface PcpOrdemItemRequest {
  produtoId: number;
  quantidadePrevista: number;
  custoUnitarioPrevisto?: number;
  etapaId?: number;
}

export interface CriarPcpOrdemRequest {
  dtEmissao: string;
  dtPrevisaoInicio: string;
  dtPrevisaoTermino: string;
  dtPrevisaoEntrega: string;
  itens: PcpOrdemItemRequest[];
  clienteId?: number;
  isPedidoInterno?: boolean;
  observacao?: string;
  referencia?: string;
}

export interface AtualizarPcpOrdemRequest {
  isIniciado?: boolean;
  isFinalizado?: boolean;
  isCancelado?: boolean;
  isParalizado?: boolean;
  isSatisfatorio?: boolean;
  notaAvaliacao?: number;
  dtInicio?: string;
  dtFim?: string;
  observacao?: string;
  inconformidades?: string;
}

export interface ConcluirPcpOrdemItemRequest {
  ordemItemId: number;
  quantidadeProduzida: number;
}

export interface ConcluirPcpOrdemRequest {
  itens?: ConcluirPcpOrdemItemRequest[];
}

export interface ConcluirPcpOrdemResponse {
  ordemId: number;
  itensProduzidos: number;
  insumosBaixados: number;
  quantidadeProduzida: number;
  gerouEntradaAcabado: boolean;
  gerouSaidaInsumos: boolean;
}

export type StatusPcp = 'aberta' | 'em_andamento' | 'pausada' | 'finalizada' | 'cancelada';

export function calcularStatusPcp(o: PcpOrdemResumo): StatusPcp {
  if (o.isCancelado)  return 'cancelada';
  if (o.isFinalizado) return 'finalizada';
  if (o.isParalizado) return 'pausada';
  if (o.isIniciado)   return 'em_andamento';
  return 'aberta';
}

// ════════════════════════════════════════════════════════════════════════════
//  REQUISIÇÃO DE MATERIAIS
// ════════════════════════════════════════════════════════════════════════════

export interface RequisicaoResumo {
  id: number;
  dtMovimento: string;
  usuarioId: number;
  isAprovada: boolean;
  isRealizada: boolean;
  setorSolicitacaoId: number;
  ordemProducaoId: number;
  dtAgendamento: string;
  horaAgendamento?: string;
  observacao?: string;
  dtCriacao: string;
}

export interface RequisicaoItem {
  id: number;
  requisicaoId: number;
  subProdutoId: number;
  quantidade: number;
  isEfetivado: boolean;
  motivo?: string;
}

export interface RequisicaoDetalhe extends RequisicaoResumo {
  itens: RequisicaoItem[];
}

export interface RequisicaoItemRequest {
  subProdutoId: number;
  quantidade: number;
  motivo?: string;
}

export interface CriarRequisicaoRequest {
  dtMovimento: string;
  dtAgendamento: string;
  itens: RequisicaoItemRequest[];
  setorSolicitacaoId?: number;
  ordemProducaoId?: number;
  horaAgendamento?: string;
  observacao?: string;
}

export interface AtualizarRequisicaoRequest {
  isAprovada?: boolean;
  isRealizada?: boolean;
  observacao?: string;
}

export type StatusRequisicao = 'pendente' | 'aprovada' | 'realizada';

export function calcularStatusRequisicao(r: RequisicaoResumo): StatusRequisicao {
  if (r.isRealizada) return 'realizada';
  if (r.isAprovada)  return 'aprovada';
  return 'pendente';
}
